// FactCheckPanel ("Fact check") — the factual review of one material:
// how many of its questions / passages a knowledge-bank claim backs, and
// which are UNSUPPORTED (knowledge-bank.md §11.6; FR-6.6, QA-12). Mounted in
// the right dock's focused-material view, fed by the pure adapter in
// `reviewQualityModel.ts` (the coverage is the server's claim-support tally,
// computed, never stored).
//
// Owner decision 2026-10-01: the word-overlap percentage ("91% textual
// anchoring") is gone — it never said "of what" or "what to do". Coverage
// does: "4 of 5 questions are backed" + the ones without. A backed statement
// expands to its claims, each opening its source passage (FR-13.9).
//
// Pure: `factCheckRead` folds the view-model into message descriptors; the
// component only localises and lays out. No state, no I/O, no borders.

import type * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { type ClaimIntents, ClaimList } from "../ClaimRow/ClaimRow.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import type { StatementBacking } from "../QualitySummary/QualitySummary.js";
import { Text } from "../Text/Text.js";
import "./FactCheckPanel.css";

// === View-model ==============================================================

// A source the evaluator consulted.
export type ConsultedSource = {
    readonly id: string;
    readonly title: string;
};

export type FactCheckViewModel =
    | { readonly type: "not_checked" }
    | {
          readonly type: "checked";
          // Per kind: the items a claim backs and the unsupported ones.
          readonly coverage: Core.Quality.CitationCoverage.CitationCoverage;
          // Each statement a knowledge-bank claim backs, with its claims.
          readonly backed: readonly StatementBacking[];
          readonly sources: readonly ConsultedSource[];
      };

export type FactCheckPanelProps = {
    readonly model: FactCheckViewModel;
    // Runs the evaluator now — offered only in the not-checked state.
    readonly onCheckNow?: () => void;
    // Opens the material at an uncited item (its node id). Absent ⇒ the items
    // read as plain text.
    readonly onOpenItem?: (id: string) => void;
    // A backed statement expands to its claims (FR-13.9); absent ⇒ the
    // backed statements are not listed.
    readonly claimIntents?: ClaimIntents;
};

// === Verdict — ONE derivation the headline and body share ===================

// `no_claim_items`: checked, but the material asks/asserts nothing a source
// could back (only headings, say). `all_good` needs every item backed — so a
// material with no backing can never read as clean (F109).
export type PanelVerdict =
    | "not_checked"
    | "no_claim_items"
    | "findings"
    | "all_good";

const uncitedOf = (
    coverage: Core.Quality.CitationCoverage.CitationCoverage,
): readonly Core.Quality.CitationCoverage.FactualItem[] =>
    coverage.flatMap((k) => k.uncited);

export const verdictOf = (model: FactCheckViewModel): PanelVerdict => {
    if (model.type === "not_checked") return "not_checked";
    if (model.coverage.length === 0) return "no_claim_items";
    return uncitedOf(model.coverage).length > 0 ? "findings" : "all_good";
};

// === Projection — view-model → what to say ==================================

export type FactCheckRead = {
    readonly verdict: PanelVerdict;
    // One line per kind ("4 of 5 questions are backed"), or the
    // verdict's own headline when there is nothing to count.
    readonly headlines: readonly MessageDescriptor[];
    readonly subtext: MessageDescriptor | null;
    // The unsupported items, named by their opening words.
    readonly uncited: readonly UncitedRow[];
};

type UncitedRow = {
    readonly id: string;
    readonly excerpt: string;
};

const EXCERPT_WORDS = 12;

// The item's opening words — enough to recognise it, never a wall of text.
export const excerptOf = (text: string, max = EXCERPT_WORDS): string => {
    const words = text
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0);
    return words.length <= max
        ? words.join(" ")
        : `${words.slice(0, max).join(" ")}…`;
};

const kindLine = (
    k: Core.Quality.CitationCoverage.KindCoverage,
): MessageDescriptor => {
    const cited = k.cited;
    switch (k.kind) {
        case "question":
            return msg`${plural(k.total, {
                one: `${cited} of # question is backed by your uploaded sources`,
                other: `${cited} of # questions are backed by your uploaded sources`,
            })}`;
        case "passage":
            return msg`${plural(k.total, {
                one: `${cited} of # passage is backed by your uploaded sources`,
                other: `${cited} of # passages are backed by your uploaded sources`,
            })}`;
        default:
            return exhaustSwitchCase(k.kind);
    }
};

export const factCheckRead = (model: FactCheckViewModel): FactCheckRead => {
    const verdict = verdictOf(model);
    if (model.type === "not_checked") {
        return {
            verdict,
            headlines: [msg`Not checked yet`],
            subtext: msg`The material hasn't been compared with its sources yet.`,
            uncited: [],
        };
    }
    const uncited = uncitedOf(model.coverage).map(
        (i): UncitedRow => ({ id: i.id, excerpt: excerptOf(i.text) }),
    );
    return {
        verdict,
        headlines:
            model.coverage.length === 0
                ? [msg`Nothing here to check against sources`]
                : model.coverage.map(kindLine),
        subtext:
            verdict === "all_good"
                ? msg`Every item is backed by your uploaded sources.`
                : verdict === "no_claim_items"
                  ? msg`The material has no questions or passages yet.`
                  : null,
        uncited,
    };
};

// === Component ===============================================================

const ICON: Record<PanelVerdict, IconName> = {
    not_checked: "anchor",
    no_claim_items: "anchor",
    findings: "warning",
    all_good: "check",
};

export const FactCheckPanel = ({
    model,
    onCheckNow,
    onOpenItem,
    claimIntents,
}: FactCheckPanelProps): ReactNode => {
    const { i18n, t } = useLingui();
    const read = factCheckRead(model);
    return (
        <section className="fgp-panel" aria-label={t`Fact check`}>
            <header className="fgp-header">
                <div className="fgp-headline">
                    <span
                        className={`fgp-headlineIcon fgp-headlineIcon--${read.verdict}`}
                        aria-hidden="true"
                    >
                        <Icon name={ICON[read.verdict]} size="sm" />
                    </span>
                    <div className="fgp-headlineText">
                        {read.headlines.map((h) => (
                            <Text key={h.id} as="p" size="md" weight="medium">
                                {i18n._(h)}
                            </Text>
                        ))}
                        {read.subtext !== null ? (
                            <Text as="p" size="sm" tone="dim">
                                {i18n._(read.subtext)}
                            </Text>
                        ) : null}
                    </div>
                </div>
                {model.type === "not_checked" && onCheckNow ? (
                    <button
                        type="button"
                        className="fgp-checkNowBtn"
                        onClick={onCheckNow}
                    >
                        <Icon name="sparkle" size="xs" />
                        {t`Check now`}
                    </button>
                ) : null}
            </header>
            {model.type === "checked" ? (
                <div className="fgp-body">
                    {read.uncited.length > 0 ? (
                        <div>
                            <Text
                                as="p"
                                size="xs"
                                tone="faint"
                                className="fgp-sectionLabel"
                            >
                                {t`Unsupported`}
                            </Text>
                            <ul className="fgp-uncitedList">
                                {read.uncited.map((row) => (
                                    <li key={row.id}>
                                        {onOpenItem !== undefined ? (
                                            <button
                                                type="button"
                                                className="fgp-uncited fgp-uncited--link"
                                                onClick={() =>
                                                    onOpenItem(row.id)
                                                }
                                            >
                                                <Text as="span" size="sm">
                                                    {row.excerpt}
                                                </Text>
                                                <Icon
                                                    name="arrow-right"
                                                    size="xs"
                                                />
                                            </button>
                                        ) : (
                                            <Text
                                                as="p"
                                                size="sm"
                                                className="fgp-uncited"
                                            >
                                                {row.excerpt}
                                            </Text>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    {claimIntents === undefined ||
                    model.backed.length === 0 ? null : (
                        <div>
                            <Text
                                as="p"
                                size="xs"
                                tone="faint"
                                className="fgp-sectionLabel"
                            >
                                {t`Backed by your uploaded sources`}
                            </Text>
                            <ul className="fgp-citedList">
                                {model.backed.map((b) => (
                                    <li key={b.node}>
                                        <details className="fgp-backed">
                                            <summary>
                                                <Text as="span" size="sm">
                                                    {excerptOf(b.text)}
                                                </Text>
                                            </summary>
                                            <ClaimList
                                                claims={b.claims}
                                                intents={claimIntents}
                                            />
                                        </details>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <SourcesRow sources={model.sources} />
                </div>
            ) : null}
        </section>
    );
};

const SourcesRow = ({
    sources,
}: {
    readonly sources: readonly ConsultedSource[];
}): ReactNode => {
    const { t } = useLingui();
    return sources.length === 0 ? null : (
        <div>
            <Text as="p" size="xs" tone="faint" className="fgp-sectionLabel">
                {t`Sources compared`}
            </Text>
            <ul className="fgp-sourceList">
                {sources.map((s) => (
                    <li key={s.id} className="fgp-sourceChip">
                        <Icon name="source" size="xs" />
                        <Text as="span" size="xs" className="fgp-sourceTitle">
                            {s.title}
                        </Text>
                    </li>
                ))}
            </ul>
        </div>
    );
};
